import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toPastTenseAction } from '../../../utils/actionLabel'
import {
  Button,
  message,
  Modal,
  Spin,
  Tabs,
} from '@surecompanies/core_components'
import EditOutlined from '@ant-design/icons/EditOutlined'
import PlanBenefitOverrideForm, {
  type ServerFieldError,
} from './PlanBenefitOverrideForm'
import OverrideHeaderChips from './OverrideHeaderChips'
import {
  apiParamsToFormParams,
  type PlanBenefitOverrideFormValues,
  type ViewMetadata,
} from './planBenefitOverrideForm.schema'
import type {
  CreatePlanBenefitOverrideRuleRequestDto,
  UpdatePlanBenefitOverrideRuleRequestDto,
} from '../../../api/dto/plan-benefit-override-rules.dto'
import { PlanBenefitOverrideRuleStatus } from '../enums/plan-benefit-override-rule.enum'
import { useGetOverrideRule } from '../hooks/useGetOverrideRule'
import { useToggleOverrideRuleStatus } from '../hooks/useToggleOverrideRuleStatus'
import { useUpdateOverrideRule } from '../hooks/useUpdateOverrideRule'
import {
  extractFieldErrors,
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../../utils/apiErrors'
import DocumentSection from '../../../components/DocumentSection'
import OverrideHistoryTimeline from '../../../components/OverrideHistoryTimeline'
import OverrideAuditStrip from './OverrideAuditStrip'
import { useEditDocumentStaging } from '../../../hooks/useEditDocumentStaging'

export type PlanBenefitOverrideModalMode = 'view' | 'edit'

interface Props {
  ruleId: string | null
  initialMode?: PlanBenefitOverrideModalMode
  onClose: () => void
  // Fired after a successful Save Changes. Parent uses this to switch the
  // list's active tab to where the saved rule lives, so the user sees the
  // updated row immediately after the modal closes.
  onSaved?: (status: PlanBenefitOverrideRuleStatus) => void
  canEdit?: boolean
}

export default function PlanBenefitOverrideModal({
  ruleId,
  initialMode = 'view',
  onClose,
  onSaved,
  canEdit = true,
}: Props) {
  const open = ruleId !== null
  const [mode, setMode] = useState<PlanBenefitOverrideModalMode>(initialMode)
  const [activeTab, setActiveTab] = useState('details')
  const [serverErrors, setServerErrors] = useState<ServerFieldError[]>([])
  const {
    data: rule,
    isLoading,
    error,
  } = useGetOverrideRule(ruleId ?? undefined)
  const { mutateAsync, isPending: isUpdatePending } = useUpdateOverrideRule(
    ruleId ?? ''
  )
  const { mutateAsync: toggleStatus, isPending: isTogglePending } =
    useToggleOverrideRuleStatus()
  const isPending = isUpdatePending || isTogglePending
  const [isSubmitLocked, setIsSubmitLocked] = useState(false)
  const submitLockRef = useRef(false)
  // Edit-mode staging: trash + drop only mutate the buffer; Save commits.
  const staging = useEditDocumentStaging({ ruleId: ruleId ?? '' })
  const isBusy = isPending || staging.isCommitting || isSubmitLocked

  // Parent's `initialMode` must win on every re-open.
  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setActiveTab('details')
      setServerErrors([])
    }
  }, [open, initialMode, ruleId])

  // Reset staged doc changes whenever the modal switches rules or closes,
  // so a fresh open never starts with leftover staging from a previous rule.
  // Intentionally only reacts to ruleId — `staging.discard` is stable.
  const stagingDiscard = staging.discard
  useEffect(() => {
    stagingDiscard()
  }, [ruleId, stagingDiscard])

  const defaultValues = useMemo<
    Partial<PlanBenefitOverrideFormValues> | undefined
  >(() => {
    if (!rule) return undefined
    return {
      template: rule.template,
      hiosPlanId: rule.match.hiosPlanId,
      year: rule.match.year,
      benefitName: rule.match.benefitName,
      sourceUrl: rule.sourceUrl ?? '',
      notes: rule.notes ?? '',
      params: apiParamsToFormParams(rule.template, rule.params),
    } as Partial<PlanBenefitOverrideFormValues>
  }, [rule])

  const metadata = useMemo<ViewMetadata | undefined>(() => {
    if (!rule) return undefined
    return {
      status: rule.status,
      createdAt: rule.createdAt,
      createdByEmail: rule.createdByEmail,
      updatedAt: rule.updatedAt,
      updatedByEmail: rule.updatedByEmail,
    }
  }, [rule])

  const closeModal = () => {
    if (isBusy) return
    if (staging.hasStagedChanges) {
      Modal.confirm({
        title: 'Discard unsaved document changes?',
        content:
          'You have pending uploads or removals that have not been saved. Discard them?',
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        onOk: () => {
          staging.discard()
          setServerErrors([])
          onClose()
        },
      })
      return
    }
    setServerErrors([])
    onClose()
  }

  const handleClose = closeModal

  const handleSwitchToEdit = () => {
    setServerErrors([])
    setMode('edit')
  }

  // Cancel always closes — falling back to view mode confused users.
  const handleCancelEdit = closeModal

  const handleStatusChange = (toStatus: PlanBenefitOverrideRuleStatus) => {
    const actionLabel =
      toStatus === PlanBenefitOverrideRuleStatus.APPROVED
        ? 'Approve'
        : toStatus === PlanBenefitOverrideRuleStatus.REJECTED
          ? 'Reject'
          : 'Re-draft'
    Modal.confirm({
      title: `${actionLabel} override`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} this override?`,
      okText: actionLabel,
      okButtonProps: {
        danger: toStatus === PlanBenefitOverrideRuleStatus.REJECTED,
      },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await toggleStatus({ id: ruleId ?? '', status: toStatus })
          message.success(`Override ${toPastTenseAction(actionLabel)}.`)
          if (rule) onSaved?.(toStatus)
          onClose()
        } catch {
          message.error(
            `Failed to ${actionLabel.toLowerCase()} override. Please try again.`
          )
        }
      },
    })
  }

  const handleApprove = () =>
    handleStatusChange(PlanBenefitOverrideRuleStatus.APPROVED)
  const handleReject = () =>
    handleStatusChange(PlanBenefitOverrideRuleStatus.REJECTED)
  const handleRedraft = () =>
    handleStatusChange(PlanBenefitOverrideRuleStatus.DRAFT)

  const handleSubmit = async (
    body: CreatePlanBenefitOverrideRuleRequestDto
  ) => {
    if (submitLockRef.current || isBusy) return
    submitLockRef.current = true
    setIsSubmitLocked(true)
    setServerErrors([])
    const updateBody: UpdatePlanBenefitOverrideRuleRequestDto = {
      params: body.params,
      sourceUrl: body.sourceUrl ?? null,
      notes: body.notes ?? null,
    }
    try {
      await mutateAsync(updateBody)
      // Commit staged doc uploads + deletes AFTER field update succeeds.
      // If commit throws, surface a generic error but keep modal open so the
      // user can retry — the rule changes are already persisted.
      try {
        await staging.commit()
      } catch (commitErr) {
        // eslint-disable-next-line no-console
        console.error('staged document commit failed', commitErr)
        message.error(
          'Override saved, but some document changes failed. Please retry.'
        )
        return
      }
      message.success('Override updated.')
      if (rule) onSaved?.(rule.status)
      onClose()
    } catch (err) {
      const status = getApiErrorStatus(err)

      if (status === 400 || status === 422) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors.length > 0) {
          setServerErrors(fieldErrors)
          return
        }
        message.error(
          getApiErrorMessage(
            'Some fields look incorrect. Please review and try again.'
          )
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('updateOverrideRule failed', err)
        message.error('An error occurred. Please try again.')
      }
    } finally {
      submitLockRef.current = false
      setIsSubmitLocked(false)
    }
  }

  const isViewMode = mode === 'view'
  const isApproved = rule?.status === PlanBenefitOverrideRuleStatus.APPROVED
  const isDraft = rule?.status === PlanBenefitOverrideRuleStatus.DRAFT

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="bodyPlaceholder">
        <Spin />
      </div>
    )
  } else if (error || !rule) {
    body = (
      <div className="bodyPlaceholder">
        <p>Override not found.</p>
      </div>
    )
  } else if (isViewMode) {
    body = (
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <>
                <PlanBenefitOverrideForm
                  mode={mode}
                  defaultValues={defaultValues}
                  serverErrors={serverErrors}
                  isSubmitting={isBusy}
                  onSubmit={handleSubmit}
                  onCancel={handleCancelEdit}
                  extraContent={
                    ruleId ? <DocumentSection ruleId={ruleId} readOnly /> : null
                  }
                />
              </>
            ),
          },
          {
            key: 'history',
            label: 'History',
            children: ruleId ? (
              <OverrideHistoryTimeline
                ruleId={ruleId}
                ruleStatus={rule?.status}
                ruleTemplate={rule?.template}
              />
            ) : null,
          },
        ]}
      />
    )
  } else {
    body = (
      <PlanBenefitOverrideForm
        mode={mode}
        defaultValues={defaultValues}
        metadata={metadata}
        serverErrors={serverErrors}
        isSubmitting={isBusy}
        onSubmit={handleSubmit}
        onCancel={handleCancelEdit}
        extraContent={
          ruleId ? <DocumentSection ruleId={ruleId} staging={staging} /> : null
        }
      />
    )
  }

  const titleText = isViewMode ? 'Review Override' : 'Edit Override'
  const titleNode = (
    <div className="titleRow">
      <span className="titleLeft">
        <span className="titleText">{titleText}</span>
        {rule && <OverrideHeaderChips status={rule.status} />}
      </span>
    </div>
  )

  return (
    <Modal
      title={titleNode}
      open={open}
      onCancel={handleClose}
      width={960}
      destroyOnClose
      maskClosable={!isBusy}
      footer={
        isViewMode && rule && activeTab === 'details' ? (
          <div className="footerRow">
            {metadata && <OverrideAuditStrip metadata={metadata} />}
            <div className="footerActions">
              <Button type="default" onClick={handleClose} disabled={isBusy}>
                Close
              </Button>
              {canEdit && (
                <Button
                  type="default"
                  onClick={handleSwitchToEdit}
                  disabled={isBusy}
                >
                  <EditOutlined /> Edit
                </Button>
              )}
              {canEdit && (
                <>
                  {isApproved && (
                    <Button danger onClick={handleReject} loading={isPending}>
                      Reject
                    </Button>
                  )}
                  {!isApproved && (
                    <Button
                      type="primary"
                      onClick={handleApprove}
                      loading={isPending}
                    >
                      Approve
                    </Button>
                  )}
                  {!isDraft && (
                    <Button onClick={handleRedraft} loading={isPending}>
                      Re-draft
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {body}
    </Modal>
  )
}
